import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Button, Grid, IconButton, Stack } from '@mui/material'
import { ArrowBack, SaveAlt } from '@mui/icons-material'

import { formatContributionDescription, roundToDecimalPlaces, toAmountString, toDateStringNumeric } from '../../utils'
import { queries } from '../../graphql'
import { Box, Container, DataGrid, Icons } from '../../components'
// Same generic, RBAC-gated, menu-metadata-driven ActionButton reused on the
// Investment Account cards -- see the comment there for why this isn't
// hardcoded JSX.
import { ActionButton } from '../../management/management-components/actions/ActionButton'
import { PortfolioSummary } from './portfolio-summary'
import { RequestLiquidationDialog } from './initiate-transfer-dialog'
import { TotalBalanceCard, borderedBoxSx } from './total-balance-card'
import {
  hasUnlinkedInvestmentActivity,
  investmentTransferDescription,
  investmentTransferDirection,
  investmentTransferRowDate,
  investmentTransferStateLabel,
  isCalendarDateOnly,
  toLocalMidnight,
  wrappedLineCount,
} from './investment-activity-helpers'

// Each row's id is prefixed by source table ('ba123'/'it123'/'c123') to stay
// unique across the three row types combined below -- this strips that
// prefix back off to get the real, monotonically-increasing database primary
// key, used as a tiebreaker when two rows land on the exact same date (see
// its use in the rows useMemo and the date column's sortComparator).
const numericRowId = (id) => parseInt(String(id).replace(/^\D+/, ''), 10)

// The grid's single-line row height (see the DataGrid usage below for why
// this can't just be MUI X's own autoRowHeight). Also the per-extra-line
// height added when the Description column wraps -- see getRowHeight below.
const BASE_ROW_HEIGHT = 52
const DESCRIPTION_LINE_HEIGHT = 20
// .MuiDataGrid-cell's own left+right padding (0 10px, MUI's default) --
// subtracted from the Description column's computed width to get the actual
// width its text has to wrap within.
const DESCRIPTION_CELL_HORIZONTAL_PADDING = 20
// MUI's default cell styling vertically CENTERS each cell's content, which
// looks fine while every cell is one line -- but a wrapped Description cell
// is a taller block than its neighbours, and centering that whole block
// pushes its first line above, and its second line below, where every other
// column's single centered line sits. All cells below are top-aligned at
// this same fixed offset instead (the offset centering would've put a
// single line at, in the unwrapped case) so a row only ever grows
// downward from that shared anchor -- see the DataGrid's own sx override,
// and the Description renderCell's explicit lineHeight (has to match
// DESCRIPTION_LINE_HEIGHT exactly, or wrapped lines would slowly drift out
// of sync with the getRowHeight math that sized the row for them).
const CELL_TOP_PADDING = (BASE_ROW_HEIGHT - DESCRIPTION_LINE_HEIGHT) / 2

// Per-portfolio drill-down for the "View Activity" button on each Investment
// Account card -- lists THIS portfolio's own actual money movements: Balance
// Adjustments, Investment Transfers, and Contributions (see the rows useMemo
// below for why Investment Value Adjustments still aren't a fourth row type
// here). Any portfolioId-less Contribution or Balance Adjustment isn't
// attributable to one specific account -- those show on the separate "Other
// Investment Wallet Activity" card instead (investment-unlinked-activity.jsx)
// rather than being duplicated into every portfolio's table here.
export function InvestmentPortfolioActivity() {
  const navigate = useNavigate()
  const { fundId: fundIdParam, portfolioId: portfolioIdParam } = useParams()
  const fundId = Number(fundIdParam)
  const portfolioId = Number(portfolioIdParam)

  const { loading, data: activityData } = useQuery(queries.funds.investmentPortfolioActivity, {
    variables: { fundId },
  })

  const fund = activityData?.me?.fund
  const portfolios = fund?.investmentPortfolios || []
  const portfolio = portfolios.find((p) => p.id === portfolioId)
  // Same "how many cards' worth of activity does this donor have" count the
  // index page redirects on -- if this portfolio is the only one, there's
  // nothing else to go Back to (Back would just land on the index, which
  // immediately redirects right back here), so the arrow doesn't render.
  const isOnlyCard = portfolios.length + (hasUnlinkedInvestmentActivity(fund) ? 1 : 0) === 1
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)

  // Populated via DataGrid's own apiRef mechanism (see data-grid.jsx) so the
  // Export button on the total balance card below can trigger the grid's
  // built-in CSV export without that button living inside the grid's own
  // toolbar -- disableExport hides DataGridToolbar's copy of it there.
  const gridApiRef = useRef(null)
  // "Investment Account Activity Export - <account number> (<firm>)"; the
  // grid appends the .csv extension. Characters Windows/macOS reject in file
  // names are stripped in case the firm name carries one.
  const firmName = portfolio?.financialFirm?.name
  const exportFileName = portfolio
    ? `Investment Account Activity Export - ${portfolio.custodialNumber}${firmName ? ` (${firmName})` : ''}`.replace(
        /[\\/:*?"<>|]/g,
        ''
      )
    : 'Investment Account Activity Export'
  const handleExport = () => gridApiRef.current?.exportDataAsCsv({ allColumns: true, fileName: exportFileName })

  // Per-row height: BASE_ROW_HEIGHT unless the Description column's own text
  // needs to wrap onto more than one line at its actual rendered width, in
  // which case the row grows by one DESCRIPTION_LINE_HEIGHT per extra line.
  // Returning a plain number here (never the string 'auto') is what keeps
  // this off MUI X's own auto-measurement path -- see the comment on
  // rowHeight below.
  const getRowHeight = useCallback(({ model }) => {
    const descriptionWidth = gridApiRef.current?.getColumn?.('description')?.computedWidth
    if (!descriptionWidth) return BASE_ROW_HEIGHT

    const lines = wrappedLineCount(model.description, descriptionWidth - DESCRIPTION_CELL_HORIZONTAL_PADDING)
    return BASE_ROW_HEIGHT + (lines - 1) * DESCRIPTION_LINE_HEIGHT
  }, [])

  // The Description column's computed width (and so its wrap point) only
  // changes when this box's own width does -- e.g. the browser window
  // resizing -- but nothing about that tells the grid's row-height cache to
  // recompute, since getRowHeight above isn't 'auto' (a plain number skips
  // MUI X's own remeasure-on-resize handling, along with the bug it comes
  // with). Watching the box directly, rather than depending on a MUI-internal
  // resize event, keeps this independent of that library's own event names.
  const activityBoxRef = useRef(null)
  useEffect(() => {
    const element = activityBoxRef.current
    if (!element) return undefined
    const observer = new ResizeObserver(() => gridApiRef.current?.resetRowHeights())
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const rows = useMemo(() => {
    if (!portfolio) return []

    // Investment Value Adjustments are deliberately NOT rendered as their own
    // row here -- their Gains/Losses figure is a decomposition of the
    // reconciliation (a subset that excludes dividends/fees), not a second
    // amount that actually moved. Showing it alongside the BalanceAdjustment
    // row below (the ACTUAL ledger movement) in the same Amount column read as
    // a mismatched duplicate rather than a breakdown. That performance detail
    // is still fully available on the admin-side Investment Value Adjustment
    // list; this donor-facing feed sticks to real money movements.
    const balanceAdjustmentRows = (portfolio.balanceAdjustments || [])
      // Once money actually moves (In Transit deduct / Complete credit), the
      // resulting ledger row is suppressed here -- the transfer's own row
      // below already shows that same money movement, tagged with its
      // current state, so showing both would read as a duplicate.
      .filter((ba) => !ba.investmentTransferId)
      .map((ba) => ({
        id: `ba${ba.id}`,
        __typename: 'BalanceAdjustment',
        // Most BalanceAdjustment rows carry a genuine "posted right now" moment
        // (correctly shown in local time), but ones backdated to match a
        // statementDate/effectiveDate elsewhere on the same record are really a
        // calendar date wearing a timestamp's clothes -- see isCalendarDateOnly.
        date: isCalendarDateOnly(ba.createdAt) ? toLocalMidnight(ba.createdAt) : ba.createdAt,
        type: 'Balance Adjustment',
        description: ba.description || 'Balance Adjustment',
        amount: ba.amount,
        // Same 'posted' convention giving-wallet.jsx already uses for its own
        // BalanceAdjustment rows -- a ledger row is always a done deal, no
        // in-between state of its own.
        state: 'posted',
        // A ledger row IS the settled money movement itself, so its whole
        // amount moves the running balance -- see balanceEffect's use in the
        // balance calculation below.
        balanceEffect: ba.amount,
      }))

    // portfolio.balance is maintained as this portfolio's BalanceAdjustments
    // plus its cleared Contributions' netAmount -- an Investment Transfer
    // only ever moves it through the BalanceAdjustment rows its flow posts
    // (deduction, credit, amount correction, cancellation reversal), each
    // tagged with investmentTransferId. A transfer row's own effect on the
    // running balance is therefore the sum of its ledger rows on THIS
    // portfolio, not its requested amount gated on currentStep: that covers
    // every flow version's posting point (the pre-FLIGHT-604 flow deducted at
    // In Transit), a corrected amount, and a reversal, and makes the final
    // running balance equal portfolio.balance by construction.
    const transferLedgerTotals = (portfolio.balanceAdjustments || [])
      .filter((ba) => ba.investmentTransferId)
      .reduce(
        (totals, ba) => totals.set(ba.investmentTransferId, (totals.get(ba.investmentTransferId) || 0) + ba.amount),
        new Map()
      )

    // Investment Transfers show as their own row from the moment they're
    // Requested, before any BalanceAdjustment exists -- reversing the
    // earlier "don't duplicate the ledger row" exclusion, now that this row
    // carries state info the ledger row never did and the ledger row above
    // is suppressed once this one applies.
    const transferRows = (fund?.investmentTransfers || [])
      .map((t) => ({ transfer: t, direction: investmentTransferDirection(t, { portfolioId: portfolio.id }) }))
      .filter(({ direction }) => direction)
      .map(({ transfer: t, direction }) => ({
        id: `it${t.id}`,
        __typename: 'InvestmentTransfer',
        date: investmentTransferRowDate(t),
        type: 'Investment Transfer',
        description: investmentTransferDescription(t, direction),
        amount: direction === 'outgoing' ? -t.amount : t.amount,
        state: investmentTransferStateLabel(t),
        // Shows at every stage (Requested through Complete) so the donor can
        // track it, but only moves the running balance by what has actually
        // been posted to the ledger for it -- see transferLedgerTotals above.
        // A transfer with nothing posted yet (Requested/Approved) moves it by 0.
        balanceEffect: transferLedgerTotals.get(t.id) || 0,
      }))

    const contributionRows = (portfolio.contributions || []).map((c) => ({
      id: `c${c.id}`,
      __typename: 'Contribution',
      date: c.receiptedDate,
      type: 'Contribution',
      description: formatContributionDescription(c),
      amount: c.amount,
      // A pending/failed Contribution hasn't actually settled, so only a
      // 'cleared' one moves the running balance -- and by its netAmount, the
      // figure ManageContributionsService credits to portfolio.balance, not
      // the gross amount shown in the Amount column.
      balanceEffect: c.state === 'cleared' ? c.netAmount : 0,
      state: c.state,
    }))

    // Normalized to a real Date object here, once, for every row regardless
    // of which block built it -- balanceAdjustmentRows/transferRows each only
    // convert to a Date in their isCalendarDateOnly branch, leaving the other
    // branch (and contributionRows always) a raw ISO string. Left mixed, the
    // grid's own Date column (no `type: 'date'` below otherwise) falls back
    // to string-comparing values, and a Date object's default toString()
    // sorts nothing like an ISO string does -- silently wrong "chronological"
    // display order for any row's date that came through as an object.
    const allRows = [...balanceAdjustmentRows, ...transferRows, ...contributionRows].map((row) => ({
      ...row,
      date: new Date(row.date),
    }))

    // Running balance is a property of each row's chronological position, not
    // of whatever order/sort the grid happens to be displaying (initialState
    // below sorts by date desc) -- so it's computed here, ascending by date,
    // independent of display order. Starts at 0, NOT portfolio.openingBalance
    // -- InvestmentPortfoliosService already posts the opening balance (and
    // any later change to it) as its own BalanceAdjustment ledger row with no
    // investmentTransferId, so it's already one of allRows above; seeding the
    // baseline with openingBalance too would double-count it. Rounded after
    // each step to avoid float drift from summing many rows' amounts
    // client-side.
    // Backdated rows can legitimately share the exact same calendar date --
    // e.g. an Investment Value Adjustment's reconciliation date coinciding
    // with the portfolio's own opening-balance effectiveDate -- which ties
    // `a.date - b.date` at 0. Array.sort is stable, so without a tiebreaker
    // that tie would silently resolve to whatever arbitrary order the
    // GraphQL response happened to return the rows in, not which one
    // actually happened first. numericRowId is a real, monotonically-
    // increasing database primary key, so it reconstructs true creation
    // order for a same-source tie (the case that actually surfaced this) and
    // is at least deterministic for the rarer cross-source tie. The date
    // column's own sortComparator below applies this identical tiebreak, so
    // the grid's on-screen/export order always agrees with this calculation.
    // Every row still appears in the table/export regardless of state -- each
    // row shifts the running balance by its own balanceEffect (what it has
    // actually settled into portfolio.balance), which is 0 for a pending
    // Contribution or a transfer with nothing posted yet. Such a row is left
    // with whatever the balance already was going into it, same as any other
    // reference point that doesn't itself move money.
    let runningBalance = 0
    const balanceByRowId = new Map(
      [...allRows]
        .sort((a, b) => a.date - b.date || numericRowId(a.id) - numericRowId(b.id))
        .map((row) => {
          runningBalance = roundToDecimalPlaces(runningBalance + row.balanceEffect)
          return [row.id, runningBalance]
        })
    )

    return allRows.map((row) => ({ ...row, balance: balanceByRowId.get(row.id) }))
  }, [fund, portfolio])

  const columns = [
    {
      field: '__typename',
      headerName: 'Transaction',
      pinnable: true,
      hideable: true,
      flex: 0.5,
      align: 'center',
      minWidth: 50,
      renderCell: ({ row } = {}) => <Icons.ActivityItem row={row} />,
      renderHeader: () => null,
    },
    {
      field: 'date',
      headerName: 'Date',
      // Without this, MUI falls back to its generic (effectively
      // string-based) comparator -- see the comment on allRows above for why
      // that silently disagreed with the Balance column's own date-aware
      // sort once a row's date came through as a Date object rather than
      // a string.
      type: 'date',
      // Same tiebreak as the Balance calculation above (numericRowId), so two
      // rows sharing the exact same backdated date always land in the same
      // relative order here as they did when the running balance was
      // computed -- otherwise ties could display in one order while their
      // Balance values reflect the other.
      sortComparator: (v1, v2, cellParams1, cellParams2) =>
        v1 - v2 || numericRowId(cellParams1.id) - numericRowId(cellParams2.id),
      flex: 0.75,
      // yyyy-MM-dd (toDateStringNumeric) is a fixed-length format -- 100px
      // comfortably covers it with room to spare, so some of this column's
      // old slack was reclaimed for State below.
      minWidth: 100,
      valueFormatter: ({ value } = {}) => toDateStringNumeric(value),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1.25,
      // Fixed 3-value set (Balance Adjustment / Investment Transfer /
      // Contribution, set in the rows useMemo above) -- same "known,
      // bounded content" reasoning as State, so this had real slack at 200
      // (needs ~145px) to give up.
      minWidth: 150,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2.5,
      // Reclaimed a bit more for State below -- safe to shrink further than
      // Date/Type since this is the one column that wraps instead of
      // clipping if it ever runs short on room (see renderCell/getRowHeight).
      minWidth: 215,
      // The only column allowed to wrap -- see getRowHeight above, which
      // grows the row to fit whatever this measures out to. Every other
      // column keeps the grid's default single-line, ellipsis-on-overflow
      // cell. lineHeight is explicit, not the font's own default, so the
      // browser's real wrapped line spacing matches what getRowHeight
      // assumed when it sized the row (see DESCRIPTION_LINE_HEIGHT).
      renderCell: ({ value } = {}) => (
        <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: `${DESCRIPTION_LINE_HEIGHT}px` }}>
          {value}
        </Box>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      minWidth: 130,
      // FLIGHT-295 follow-up: never show a leading "-" on screen -- the
      // underlying signed `amount` still drives Icons.ActivityItem's
      // credit/debit icon (see the Transaction column above). The CSV export
      // has no such icon, so valueFormatter (which the export reads, not
      // renderCell) keeps the sign there -- otherwise a negative Balance
      // Adjustment exports indistinguishable from a positive one.
      valueFormatter: ({ value } = {}) => toAmountString(value),
      renderCell: ({ value } = {}) => toAmountString(Math.abs(value)),
    },
    {
      // Export-only (see initialState.columns.columnVisibilityModel below) --
      // FLIGHT-550 asked for the running balance on the export, not the
      // on-screen table itself. hideable: false keeps it out of a column
      // menu's "unhide" list, on top of not rendering a header at all.
      field: 'balance',
      headerName: 'Balance',
      hideable: false,
      flex: 1,
      minWidth: 130,
      valueFormatter: ({ value } = {}) => toAmountString(value),
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 1,
      // Every possible label (STEP_STATE_LABEL in investment-activity-
      // helpers.js, plus the literal 'posted' BalanceAdjustment rows use)
      // is a known, bounded set -- measured against the grid's actual font,
      // the longest ("Approved: Pending", ~119px text) plus the pill's
      // icon/spacing/padding and the cell's own padding needs ~178px. 190
      // covers that with a small safety margin, so this column can never be
      // squeezed narrower than its content without either wrapping (which
      // the pill doesn't support) or clipping (Icons.State's renderCell
      // bypasses MUI's own ellipsis handling entirely, so an overflow here
      // would be a silent hard cutoff, not a "...").
      minWidth: 190,
      renderCell: ({ value } = {}) => <Icons.State state={value} />,
    },
  ]

  // Back arrow + the full portfolio summary (Financial Advisor/Firm/Advisory
  // Team/Balance/Last Statement Date) -- its own bordered card, same as a
  // portfolio's card on the Investment Account tab's list
  // (investment-account.jsx's PortfolioCard), not folded into the DataGrid's
  // title slot alongside the table. The arrow itself is omitted when this
  // portfolio is the donor's only card -- see isOnlyCard.
  const portfolioDetails = portfolio && (
    <Stack direction="row" alignItems="flex-start" spacing={1}>
      {!isOnlyCard && (
        <IconButton size="small" onClick={() => navigate('..')}>
          <ArrowBack />
        </IconButton>
      )}
      <PortfolioSummary portfolio={portfolio} />
    </Stack>
  )

  return (
    <Container maxWidth="lg">
      {/* Grid, not a Stack -- every Stack in this app defaults to
          alignItems: 'flex-start' (see the MuiStack defaultProps override in
          mui.config.js), which shrinks a Stack's children to their own
          content width instead of stretching them to fill it. A Grid item
          at xs={12} always spans the full row by design, sidestepping that
          default entirely -- the same reason the Investment Account tab's
          list (investment-account.jsx) uses Grid here instead of Stack.
          Total balance card, same as that list -- the FLIGHT-296 mockup
          shows it above the activity table here too, not just above the
          portfolio cards on the multi-account page. The table below sits in
          a plain bordered Box (borderedBoxSx), not a CardBox -- matching
          that same page's format, where every card (total, each portfolio)
          is a plain border with no shadow, rather than mixing that look
          with CardBox's shadow/white panel here. */}
      {/* Explicit `key`s on every top-level Grid item -- the portfolio
          details card below doesn't exist on the very first render (it only
          appears once the query resolves and `portfolio` goes from
          undefined to truthy), so without keys React reconciles these by
          index: it'd mutate the DOM node that WAS the table's Grid item
          into becoming the portfolio details card once this one appears,
          rather than recognizing it as a genuine insertion. That kind of
          same-slot content swap is a known source of stale/torn paint until
          something forces a reflow (matches a mark that clears on scroll or
          a DevTools resize but survives a hard refresh, since the bug
          reproduces fresh every time the query resolves). */}
      <Grid container spacing={2}>
        <Grid item xs={12} key="total-balance">
          {/* This portfolio's own balance, not the fund's investment wallet
              total across every portfolio -- the table below only lists
              THIS portfolio's transactions, so the total above it needs to
              match that scope. A donor with multiple portfolios can land
              here for any one of them (from that card's own "View activity"
              on the list page), and a fund-wide total would silently
              include money movements this table doesn't show. */}
          <TotalBalanceCard
            amount={portfolio?.balance}
            actions={
              portfolio && (
                <Stack direction="row" spacing={2} sx={{ width: 'auto' }}>
                  {/* No variant override -- ActionButton already defaults to
                      "contained", same as the Export Activity button below
                      (its own unstyled default), so the two match. */}
                  {/* FLIGHT-604 renamed/retired donor.investment_transfers.initiate_transfer
                      -- request_liquidation is its direct replacement here. */}
                  <ActionButton
                    actionKey="donor.investment_transfers.request_liquidation"
                    onClick={() => setTransferDialogOpen(true)}
                  />
                  <Button startIcon={<SaveAlt />} onClick={handleExport}>
                    Export Activity
                  </Button>
                </Stack>
              )
            }
          />
        </Grid>
        {portfolioDetails && (
          <Grid item xs={12} key="portfolio-details">
            {/* Same padding as a portfolio's own card on the Investment
                Account tab's list (PortfolioCard, investment-account.jsx). */}
            <Box sx={{ ...borderedBoxSx, py: 3, px: 5 }}>{portfolioDetails}</Box>
          </Grid>
        )}
        <Grid item xs={12} key="activity-table">
          <Box ref={activityBoxRef} sx={{ ...borderedBoxSx, padding: 4 }}>
            {/* rowHeight, not autoRowHeight -- MUI X v5.12's dynamic row
                height (getRowHeight: 'auto', what autoRowHeight turns on)
                measures each row's natural height via an off-screen clone
                before applying it, and that measurement scaffolding can
                leak into view on this page (confirmed via DOM inspection: a
                0-width, overflow: visible wrapper around the real,
                full-width row content) instead of staying properly hidden.
                52 is MUI's own baseline single-line row height, used as a
                fallback for any row getRowHeight can't yet size (columns not
                laid out). getRowHeight itself grows a row past that only for
                its own reason (the Description column wrapping) --
                sidesteps the buggy code path entirely rather than patching
                around it. */}
            <DataGrid
              apiRef={gridApiRef}
              rows={rows}
              columns={columns}
              disableExport
              rowHeight={BASE_ROW_HEIGHT}
              getRowHeight={getRowHeight}
              loading={loading}
              initialState={{
                sorting: { sortModel: [{ field: 'date', sort: 'desc' }] },
                // Balance is export-only, per FLIGHT-550 -- kept in `columns`
                // so the explicit exportDataAsCsv({ allColumns: true }) call
                // above (Export Activity button, via gridApiRef) still
                // includes it, but hidden from the on-screen grid itself.
                columns: { columnVisibilityModel: { balance: false } },
              }}
              // Top-align every cell at the same fixed offset instead of
              // MUI's default vertical centering -- see CELL_TOP_PADDING.
              // Without this, a wrapped Description cell centers its whole
              // two-line block, putting its first line above (and second
              // line below) where every other column's single centered line
              // sits in that same, now-taller row.
              sx={{ '& .MuiDataGrid-cell': { alignItems: 'flex-start', pt: `${CELL_TOP_PADDING}px` } }}
            />
          </Box>
        </Grid>
      </Grid>
      {portfolio && (
        <RequestLiquidationDialog
          open={transferDialogOpen}
          onClose={() => setTransferDialogOpen(false)}
          fundId={fundId}
          portfolio={portfolio}
        />
      )}
    </Container>
  )
}
